import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tempSessionAPI } from '@/lib/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, Loader2, Download, UserPlus, Search, Users, CheckCircle,
  XCircle, Calendar, Clock, Filter, Save, MessageSquare, FileText, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function TempSessionAttendancePage() {
  const { workspaceId, sessionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState(null);
  const [dateFilter, setDateFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({
    candidate_name: '', candidate_email: '', candidate_phone: '', attendance_date: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [session, setSession] = useState(null);
  const [attendanceMessage, setAttendanceMessage] = useState('');
  const [savingMessage, setSavingMessage] = useState(false);
  const [dayMessages, setDayMessages] = useState({});
  const [savingDayMessages, setSavingDayMessages] = useState(false);

  useEffect(() => { loadAttendance(); loadSession(); }, [sessionId, dateFilter, nameFilter]);

  const loadSession = async () => {
    try {
      const res = await tempSessionAPI.get(workspaceId, sessionId);
      setSession(res.data);
      setAttendanceMessage(res.data?.attendance_message || '');
      setDayMessages(res.data?.attendance_day_messages || {});
    } catch (err) {
      console.error('Error loading session:', err);
    }
  };

  const handleSaveMessage = async () => {
    setSavingMessage(true);
    try {
      await tempSessionAPI.update(workspaceId, sessionId, { attendance_message: attendanceMessage });
      toast.success('Message sauvegardé');
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSavingMessage(false);
    }
  };

  const handleSaveDayMessages = async () => {
    setSavingDayMessages(true);
    try {
      await tempSessionAPI.update(workspaceId, sessionId, { attendance_day_messages: dayMessages });
      toast.success('Messages sauvegardés');
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSavingDayMessages(false);
    }
  };

  const loadAttendance = async () => {
    try {
      const params = {};
      if (dateFilter) params.date_filter = dateFilter;
      if (nameFilter) params.name_filter = nameFilter;
      const res = await tempSessionAPI.getAttendance(workspaceId, sessionId, params);
      setAttendance(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await tempSessionAPI.exportAttendance(workspaceId, sessionId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `presences_${sessionId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (err) {
      toast.error('Erreur d\'export');
    }
  };

  const handleExportPDF = async (date = null) => {
    try {
      const res = await tempSessionAPI.exportAttendancePDF(workspaceId, sessionId, date);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const suffix = date ? `_${date}` : '';
      a.download = `emargement_${sessionId}${suffix}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (err) {
      toast.error('Erreur d\'export PDF');
    }
  };

  const handleExportDayCSV = async (date) => {
    try {
      const res = await tempSessionAPI.exportAttendance(workspaceId, sessionId, date);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `presences_${sessionId}_${date}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV du jour téléchargé');
    } catch (err) {
      toast.error('Erreur d\'export CSV');
    }
  };

  const handleManualAdd = async () => {
    if (!manualForm.candidate_name || !manualForm.attendance_date) {
      toast.error('Le nom et la date sont requis');
      return;
    }
    setSubmitting(true);
    try {
      await tempSessionAPI.addManualAttendance(workspaceId, sessionId, manualForm);
      toast.success('Présence ajoutée');
      setShowManual(false);
      setManualForm({ candidate_name: '', candidate_email: '', candidate_phone: '', attendance_date: '' });
      loadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAttendance = async (recordId) => {
    if (!window.confirm('Supprimer cette présence ?')) return;
    setDeletingId(recordId);
    try {
      await tempSessionAPI.deleteAttendance(workspaceId, sessionId, recordId);
      toast.success('Présence supprimée');
      loadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch { return d; }
  };

  const formatTime = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch { return d; }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="page-container flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  const days = attendance?.days || [];
  const totalPresent = days.reduce((sum, d) => sum + d.present_count, 0);

  return (
    <AppLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/workspace/${workspaceId}/temp-sessions/${sessionId}`)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Feuille de Présence</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{totalPresent} confirmations au total</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowManual(true)} className="gap-1.5">
              <UserPlus className="h-4 w-4" /> Ajout manuel
            </Button>
            <Button variant="outline" onClick={() => handleExport()} className="gap-1.5">
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            <Button variant="default" onClick={() => handleExportPDF()} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700">
              <FileText className="h-4 w-4" /> Export PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom..."
              value={nameFilter}
              onChange={e => setNameFilter(e.target.value)}
              className="pl-9"
            />
          </div>
          <Input
            type="date"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="w-44"
            placeholder="Filtrer par date"
          />
          {(dateFilter || nameFilter) && (
            <Button variant="ghost" size="sm" onClick={() => { setDateFilter(''); setNameFilter(''); }}>
              <Filter className="h-4 w-4 mr-1" /> Réinitialiser
            </Button>
          )}
        </div>

        {/* Attendance Message */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              <Label className="font-medium">Message affiché sur la page publique</Label>
            </div>
            <div className="flex gap-2">
              <Textarea
                placeholder="Ex: Veuillez confirmer votre présence chaque jour avant 9h00..."
                value={attendanceMessage}
                onChange={e => setAttendanceMessage(e.target.value)}
                rows={2}
                className="flex-1"
              />
              <Button
                onClick={handleSaveMessage}
                disabled={savingMessage}
                size="sm"
                className="self-end"
              >
                {savingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Ce message sera visible par les candidats dans la section "Ma Présence" de la session publique.
            </p>
          </CardContent>
        </Card>

        {/* Days */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground">Messages par jour (visibles sur la page publique)</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveDayMessages}
            disabled={savingDayMessages}
            className="gap-1.5"
          >
            {savingDayMessages ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Sauvegarder les messages
          </Button>
        </div>
        <div className="space-y-4">
          {days.map(day => (
            <Card key={day.date} className="overflow-hidden">
              <CardHeader className="pb-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base capitalize">{formatDate(day.date)}</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Message du jour..."
                      value={dayMessages[day.date] || ''}
                      onChange={e => setDayMessages({ ...dayMessages, [day.date]: e.target.value })}
                      className="w-48 h-8 text-xs"
                    />
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {day.present_count} présent{day.present_count > 1 ? 's' : ''}
                    </Badge>
                    {day.records.length > 0 && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground gap-1"
                          onClick={() => handleExportDayCSV(day.date)}
                          title="Export CSV du jour"
                        >
                          <Download className="h-3 w-3" /> CSV
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 gap-1"
                          onClick={() => handleExportPDF(day.date)}
                          title="Export PDF du jour"
                        >
                          <FileText className="h-3 w-3" /> PDF
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {day.records.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Aucune présence enregistrée
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>#</th>
                          <th>Nom</th>
                          <th>Email</th>
                          <th>Téléphone</th>
                          <th>Heure</th>
                          <th>ID</th>
                          <th>IP</th>
                          <th>Type</th>
                          <th style={{ width: '40px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {day.records.map((r, idx) => (
                          <tr key={r.id}>
                            <td className="text-muted-foreground text-center font-mono text-xs">{idx + 1}</td>
                            <td className="font-medium">{r.candidate_name}</td>
                            <td className="text-muted-foreground">{r.candidate_email || '—'}</td>
                            <td className="text-muted-foreground">{r.candidate_phone || '—'}</td>
                            <td>
                              <span className="flex items-center gap-1 text-sm">
                                <Clock className="h-3 w-3" />
                                {formatTime(r.confirmed_at)}
                              </span>
                            </td>
                            <td>
                              <Badge variant="outline" className="font-mono text-xs">
                                {r.attendance_token}
                              </Badge>
                            </td>
                            <td className="text-xs text-muted-foreground">{r.ip_address || '—'}</td>
                            <td>
                              {r.is_manual ? (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs">Manuel</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">Auto</Badge>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
                                onClick={() => handleDeleteAttendance(r.id)}
                                disabled={deletingId === r.id}
                              >
                                {deletingId === r.id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {days.length === 0 && (
          <div className="empty-state py-16">
            <Users className="empty-state-icon" />
            <h3 className="text-lg font-semibold">Aucune donnée de présence</h3>
            <p className="text-sm text-muted-foreground">
              Les présences apparaîtront ici lorsque les candidats les confirmeront
            </p>
          </div>
        )}

        {/* Manual Add Dialog */}
        <Dialog open={showManual} onOpenChange={setShowManual}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter une présence manuellement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Nom complet *</Label>
                <Input
                  value={manualForm.candidate_name}
                  onChange={e => setManualForm({ ...manualForm, candidate_name: e.target.value })}
                  placeholder="Jean Dupont"
                />
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={manualForm.attendance_date}
                  onChange={e => setManualForm({ ...manualForm, attendance_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email (optionnel)</Label>
                <Input
                  type="email"
                  value={manualForm.candidate_email}
                  onChange={e => setManualForm({ ...manualForm, candidate_email: e.target.value })}
                  placeholder="jean@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone (optionnel)</Label>
                <Input
                  value={manualForm.candidate_phone}
                  onChange={e => setManualForm({ ...manualForm, candidate_phone: e.target.value })}
                  placeholder="+216 12 345 678"
                />
              </div>
              <Button onClick={handleManualAdd} disabled={submitting} className="w-full btn-gradient">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                {submitting ? 'Ajout...' : 'Ajouter la présence'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
